import { useTranslation } from "react-i18next";
import React, { useMemo, useState } from "react";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/PageHeader";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import {
  CANCEL_POLICY,
  GET_POLICY_DETAILS,
  RECHECK_MEMBER_STATUS,
} from "../../../utils/graphql";
import { apollo } from "../../../utils/apollo";
import SlashdataPolicyView from "./views/slashdata-policy-view";
import ShafafiyaPolicyView from "./views/shafafiya-policy-view";
import Button from "../../../components/Button";
import StatusBadge from "../../../components/StatusBadge";
import InformationColumn from "../../../components/InformationColumn";
import ButtonInternalLink from "../../../components/ButtonInternalLink";
import { toast } from "react-toastify";
import { ChartBarIcon } from "@heroicons/react/24/outline";
function PolicyView() {
  const { t } = useTranslation();
  const { policy_uuid } = useParams();
  const fetchPolicy = useQuery({
    queryKey: ["policy_view", policy_uuid],
    queryFn: async () => {
      const { data } = await apollo.query({
        query: GET_POLICY_DETAILS,
        variables: {
          policyUuid: policy_uuid,
        },
      });
      return data?.getPolicyDetails;
    },
    keepPreviousData: false,
  });
  const cancelPolicyMutation = useMutation({
    mutationKey: "cancel_policy",
    mutationFn: async () => {
      const { data } = await apollo.mutate({
        mutation: CANCEL_POLICY,
        variables: {
          policyUuid: policy_uuid,
        },
      });
      return data?.cancelPolicy;
    },
    onSuccess: (canceled) => {
      if (canceled === true) {
        fetchPolicy.refetch();
        toast.success("Policy canceled successfully");
      }
    },
  });
  const recheckStatusMutation = useMutation({
    mutationKey: "recheck_policy_status",
    mutationFn: async () => {
      const { data } = await apollo.mutate({
        mutation: RECHECK_MEMBER_STATUS,
        variables: {
          policyUuid: policy_uuid,
        },
      });
      return data?.reCheckMemberStatus;
    },
    onSuccess: (success) => {
      if (success) {
        toast.info("Status re-check initiated...");
        fetchPolicy.refetch();
      }
    },
  });
  const policy = useMemo(
    () => (fetchPolicy?.isSuccess ? fetchPolicy?.data : null),
    [fetchPolicy],
  );
  const handleCancelPolicy = () => {
    if (window.confirm("Are you sure you want to cancel this policy?")) {
      cancelPolicyMutation.mutate();
    }
  };
  const handleRecheckStatus = () => {
    recheckStatusMutation.mutate();
  };
  return (
    <DashboardLayout>
      <PageHeader
        back="/policies"
        title={t("Policy View")}
        description={t("View and manage your policies")}
      >
        <InformationColumn
          title={t("Status")}
          value={<StatusBadge status={policy?.status} />}
        />
      </PageHeader>

      {policy?.integration?.integrationType === "slashdata" && (
        <SlashdataPolicyView
          key={policy_uuid}
          loading={fetchPolicy?.isLoading}
          policy={policy}
        />
      )}

      {policy?.integration?.integrationType === "shafafiya" && (
        <ShafafiyaPolicyView
          key={policy_uuid}
          loading={fetchPolicy?.isLoading}
          policy={policy}
        />
      )}

      {!policy?.integration?.integrationType && !fetchPolicy.isLoading && (
        <div className="py-20 text-center text-gray-400">
          {t("Unknown integration type or policy data missing.")}
        </div>
      )}

      <div className="flex my-6 gap-4 items-center">
        {policy?.fileImport?.fileImportUuid && (
          <ButtonInternalLink
            href={`/policy/import/details/${policy.fileImport.fileImportUuid}`}
            className="flex items-center gap-2"
          >
            <ChartBarIcon className="w-5 h-5" />
            {t("View Batch Details")}
          </ButtonInternalLink>
        )}

        {["completed", "failed", "pending"].includes(policy?.status) && (
          <Button
            onClick={handleRecheckStatus}
            disabled={recheckStatusMutation.isLoading}
            secondary={true}
          >
            {t("Re-check Status")}
          </Button>
        )}

        {["completed"].includes(policy?.status) && (
          <Button
            onClick={handleCancelPolicy}
            disabled={cancelPolicyMutation.isLoading}
            danger
          >
            {t("Cancel Policy")}
          </Button>
        )}
      </div>
    </DashboardLayout>
  );
}
export default PolicyView;
